import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getUsersByBusinessId } from "shared/api/client";
import "./AdminProtectedRoute.css";
import {shouldUseUserAccountsExperience} from "shared/utils/userAccountSession";
import {useUserAccountsAccessProfile} from "shared/hooks/useUserAccountsAccessProfile";

type TokenPayload = {
    exp: number;
    business_id?: string;
    user_id?: string;
    job_title_id?: string | null;
    role?: string | null;
    roles?: unknown;
    user_store?: string | null;
    email?: string | null;
    auth_user_id?: string | number | null;
};

type AdminProtectedRouteProps = {
    children: React.ReactElement;
    redirectTo?: string;
};

function isLegacyAdminFromToken(decoded: TokenPayload): boolean {
    if (decoded.job_title_id && String(decoded.job_title_id).toUpperCase() === "ADMIN") return true;
    if (decoded.role && String(decoded.role).toUpperCase() === "ADMIN") return true;
    if (Array.isArray(decoded.roles)) {
        return decoded.roles.some((role) => String(role).toUpperCase() === "ADMIN");
    }
    return false;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
    children,
    redirectTo = "/workspace",
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [legacyLoading, setLegacyLoading] = useState(true);
    const [legacyAllowed, setLegacyAllowed] = useState(false);

    const token = useMemo(() => localStorage.getItem("authToken"), []);
    const decoded = useMemo(() => {
        if (!token) return null;
        try {
            return jwtDecode<TokenPayload>(token);
        } catch {
            return null;
        }
    }, [token]);
    const usesUserAccounts = shouldUseUserAccountsExperience(decoded);
    const {
        profile: userAccessProfile,
        isLoading: isUserAccessProfileLoading,
    } = useUserAccountsAccessProfile(
        usesUserAccounts,
        decoded?.auth_user_id,
        decoded?.email
    );
    const loading = usesUserAccounts ? isUserAccessProfileLoading : legacyLoading;
    const allowed = usesUserAccounts
        ? userAccessProfile?.user.isActive === true &&
            userAccessProfile.capabilities.canAccessGlobalAdminTools === true
        : legacyAllowed;

    useEffect(() => {
        if (usesUserAccounts) {
            setLegacyLoading(false);
            setLegacyAllowed(false);
            return;
        }

        let cancelled = false;
        const check = async () => {
            setLegacyLoading(true);
            setLegacyAllowed(false);

            const t = localStorage.getItem("authToken");
            if (!t) {
                if (!cancelled) setLegacyLoading(false);
                return;
            }

            try {
                const decoded = jwtDecode<TokenPayload>(t);

                // If token already contains an admin indicator, trust it.
                if (isLegacyAdminFromToken(decoded)) {
                    if (!cancelled) {
                        setLegacyAllowed(true);
                        setLegacyLoading(false);
                    }
                    return;
                }

                // Fallback: resolve current user role from the operator users list (existing mechanism).
                const userId = decoded.user_id != null ? String(decoded.user_id) : "";
                if (!userId) {
                    if (!cancelled) setLegacyLoading(false);
                    return;
                }

                const users = await getUsersByBusinessId();
                const current = users.find((u: any) => String(u?.id) === userId);
                const ok = String(current?.job_title_id ?? "").toUpperCase() === "ADMIN";
                if (!cancelled) {
                    setLegacyAllowed(ok);
                    setLegacyLoading(false);
                }
            } catch {
                if (!cancelled) setLegacyLoading(false);
            }
        };

        void check();
        return () => {
            cancelled = true;
        };
    }, [usesUserAccounts]);

    useEffect(() => {
        if (loading) return;
        if (allowed) return;
        const id = window.setTimeout(() => {
            navigate(redirectTo, { replace: true });
        }, 1800);
        return () => window.clearTimeout(id);
    }, [allowed, loading, navigate, redirectTo]);

    if (!token) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (loading) {
        return (
            <div className="no-access-page" dir="rtl">
                <div className="no-access-card">
                    <div className="no-access-title">בודקים הרשאות…</div>
                    <div className="no-access-subtitle">רק רגע.</div>
                </div>
            </div>
        );
    }

    if (!allowed) {
        return (
            <div className="no-access-page" dir="rtl">
                <div className="no-access-card">
                    <div className="no-access-title">אין הרשאה</div>
                    <div className="no-access-subtitle">
                        אין לך הרשאות Admin לעמוד זה. מעבירים אותך חזרה…
                    </div>
                    <div className="no-access-actions">
                        <button
                            type="button"
                            className="no-access-link"
                            onClick={() => navigate(redirectTo, { replace: true })}
                        >
                            חזרה
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return children;
};

export default AdminProtectedRoute;
